"use client";

import { Box, Grid, Text } from "@chakra-ui/react";
import { useTranslations } from "next-intl";
import { ScrollReveal } from "./ScrollReveal";
import { SectionLabel } from "./SectionLabel";
import { Emoji } from "@/_components/ui/Emoji/Emoji";

export function LandingWhyBento() {
  const tWhy = useTranslations("landing.redesign.why");
  const tItems = useTranslations("landing.WhyUseKnoot.items");

  return (
    <Box
      as="section"
      id="commitments"
      aria-labelledby="landing-why-heading"
      py={{ base: 16, md: 36 }}
      px={{ base: 4, md: 0 }}
      bg="neutral.50"
    >
      <Box maxW="1100px" mx="auto">
        <ScrollReveal>
          <SectionLabel>{tWhy("label")}</SectionLabel>
          <Text
            as="h2"
            id="landing-why-heading"
            fontFamily="heading"
            fontSize={{ base: "2xl", md: "4xl" }}
            fontWeight="semibold"
            letterSpacing="-1px"
            color="neutral.900"
            lineHeight={1.1}
            mb={3}
          >
            {tWhy("title_line1")}
            <br />
            {tWhy("title_line2")}
          </Text>
        </ScrollReveal>

        <ScrollReveal delay={0.1}>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={3.5}
            mt={{ base: 8, md: 12 }}
          >
            <Box
              gridColumn={{ base: "span 1", md: "span 2", lg: "span 2" }}
              borderRadius="16px"
              p={{ base: 5, md: 7 }}
              border="1px solid"
              borderColor="neutral.900"
              bg="neutral.900"
              transition="transform 0.25s, box-shadow 0.25s"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "0 16px 48px rgba(0,0,0,0.08)",
              }}
            >
              <Text
                fontFamily="var(--font-landing-syne), sans-serif"
                fontSize={{ base: "4xl", md: "52px" }}
                fontWeight="800"
                letterSpacing="-2px"
                lineHeight={1}
                color="white"
                mb={2}
              >
                10k+
              </Text>
              <Text fontSize="12px" color="whiteAlpha.600">
                {tWhy("stat_cvs_label")}
              </Text>
            </Box>

            <BentoLight
              icon={<Emoji emoji="locked-with-key" size={16} />}
              title={tItems("dataSecurity.title")}
              description={tItems("dataSecurity.description")}
            />

            <Box
              borderRadius="16px"
              p={{ base: 5, md: 7 }}
              border="1px solid"
              borderColor="primary.500"
              bg="primary.500"
              transition="transform 0.25s, box-shadow 0.25s"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "0 16px 48px rgba(0,0,0,0.08)",
              }}
            >
              <Text
                fontFamily="var(--font-landing-syne), sans-serif"
                fontSize={{ base: "3xl", md: "40px" }}
                fontWeight="800"
                letterSpacing="-2px"
                lineHeight={1}
                color="whiteAlpha.900"
                mb={2}
              >
                94%
              </Text>
              <Text fontSize="12px" color="whiteAlpha.800">
                {tWhy("stat_accuracy_label")}
              </Text>
            </Box>

            <BentoLight
              icon={<Emoji emoji="see-no-evil-monkey" size={16} />}
              title={tItems("privacy.title")}
              description={tItems("privacy.description")}
            />

            <BentoLight
              icon={<Emoji emoji="brain" size={16} />}
              title={tItems("aiIntelligence.title")}
              description={tItems("aiIntelligence.description")}
            />

            <Box
              gridColumn={{ base: "span 1", md: "span 2", lg: "span 2" }}
              borderRadius="16px"
              p={{ base: 5, md: 7 }}
              border="1px solid"
              borderColor="neutral.900"
              bg="neutral.900"
              transition="transform 0.25s, box-shadow 0.25s"
              _hover={{
                transform: "translateY(-4px)",
                boxShadow: "0 16px 48px rgba(0,0,0,0.08)",
              }}
            >
              <Text
                fontFamily="var(--font-landing-syne), sans-serif"
                fontSize={{ base: "4xl", md: "52px" }}
                fontWeight="800"
                letterSpacing="-2px"
                lineHeight={1}
                color="white"
                mb={2}
              >
                3×
              </Text>
              <Text fontSize="12px" color="whiteAlpha.600">
                {tWhy("stat_faster_label")}
              </Text>
            </Box>
          </Grid>
        </ScrollReveal>
      </Box>
    </Box>
  );
}

function BentoLight({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <Box
      borderRadius="16px"
      p={{ base: 5, md: 7 }}
      border="1px solid"
      borderColor="neutral.200"
      bg="neutral.100"
      transition="transform 0.25s, box-shadow 0.25s"
      _hover={{
        transform: "translateY(-4px)",
        boxShadow: "0 16px 48px rgba(0,0,0,0.08)",
      }}
    >
      <Box mb={3.5}>{icon}</Box>
      <Text
        fontFamily="var(--font-landing-syne), sans-serif"
        fontSize="15px"
        fontWeight="700"
        color="neutral.900"
        mb={1.5}
      >
        {title}
      </Text>
      <Text fontSize="12px" color="neutral.500" lineHeight={1.6}>
        {description}
      </Text>
    </Box>
  );
}
